import React from 'react';
import type { ScoreVariable, ScoreRange } from '../../types/forms';
import { Reveal } from '../Reveal';
import {
  Zap, Clock, Star, Trophy, AlertTriangle, CheckCircle2, XCircle,
  ArrowUp, ArrowDown, Flame, Target, TrendingUp, TrendingDown,
  ThumbsUp, ThumbsDown, Shield, Heart, Award,
} from 'lucide-react';

const ICON_MAP: Record<string, React.FC<any>> = {
  zap: Zap, clock: Clock, star: Star, trophy: Trophy,
  'alert-triangle': AlertTriangle, 'check-circle-2': CheckCircle2,
  'x-circle': XCircle, 'arrow-up': ArrowUp, 'arrow-down': ArrowDown,
  flame: Flame, target: Target, 'trending-up': TrendingUp,
  'trending-down': TrendingDown, 'thumbs-up': ThumbsUp,
  'thumbs-down': ThumbsDown, shield: Shield, heart: Heart, award: Award,
};

interface ScoreResultProps {
  scores: Record<string, { totalPoints: number; range: ScoreRange | null }>;
  variables: ScoreVariable[];
}

export const ScoreResult: React.FC<ScoreResultProps> = ({ scores, variables }) => {
  if (!scores || !variables?.length) return null;

  return (
    <div className="mt-8 space-y-4 w-full max-w-md">
      <Reveal variant="fade-up" delay={300}>
        <h3 className="text-lg font-semibold text-white text-center mb-4">
          Seu resultado
        </h3>
      </Reveal>

      {variables.map((variable, index) => {
        const score = scores[variable.id];
        if (!score) return null;

        const range = score.range;
        const bgColor = range?.color ? `${range.color}20` : 'rgba(255,255,255,0.05)';
        const textColor = range?.color || '#a3a3a3';

        const IconComponent = range?.icon
          ? ICON_MAP[range.icon] || null
          : null;

        return (
          <Reveal key={variable.id} variant="fade-up" delay={400 + index * 100}>
            <div
              className="rounded-2xl p-5 border transition-all"
              style={{
                backgroundColor: bgColor,
                borderColor: `${range?.color || '#404040'}40`,
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                {IconComponent && (
                  <IconComponent
                    className="w-5 h-5"
                    style={{ color: textColor }}
                  />
                )}
                <span className="text-sm text-neutral-400">{variable.name}</span>
              </div>

              <div className="flex items-baseline gap-2">
                <span
                  className="text-2xl font-bold"
                  style={{ color: textColor }}
                >
                  {range?.label || `${score.totalPoints} pts`}
                </span>
                {range?.label && (
                  <span className="text-sm text-neutral-500">
                    ({score.totalPoints} pts)
                  </span>
                )}
              </div>

              {range?.message && (
                <p className="mt-2 text-sm text-neutral-400">
                  {range.message}
                </p>
              )}
            </div>
          </Reveal>
        );
      })}
    </div>
  );
};
